import React from 'react';
import {
  Box,
  Tooltip,
} from '@mui/material';
import {
  ViewList as ListIcon,
  ViewModule as GridIcon,
} from '@mui/icons-material';
export type StandardViewMode = 'list' | 'grid';
export type ViewMode = StandardViewMode;

interface ViewModeToggleProps {
  standardModes: StandardViewMode[];
  activeStandardMode: StandardViewMode | null;
  customViews?: any[]; // Keep for backward compatibility but unused
  activeCustomView?: any; // Keep for backward compatibility but unused
  onStandardModeChange: (mode: StandardViewMode) => void;
  onCustomViewChange?: (view: any) => void; // Keep for backward compatibility but unused
  onManageViews?: () => void; // Keep for backward compatibility but unused
  size?: 'small' | 'medium' | 'large';
  showLabels?: boolean;
}

interface StandardModeConfig {
  id: StandardViewMode;
  label: string;
  icon: React.ReactElement;
  description: string;
}

const standardModeConfigs: Record<StandardViewMode, StandardModeConfig> = {
  list: {
    id: 'list',
    label: 'List',
    icon: <ListIcon />,
    description: 'Compact rows with metadata',
  },
  grid: {
    id: 'grid',
    label: 'Grid',
    icon: <GridIcon />,
    description: 'Card-based layout with thumbnails',
  },
};


const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  standardModes,
  activeStandardMode,
  onStandardModeChange,
  showLabels = false,
}) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        gap: '2px',
        padding: '4px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
      })}
    >
      {standardModes.map((mode) => {
        const config = standardModeConfigs[mode];
        const isActive = mode === activeStandardMode;
        return (
          <Tooltip key={mode} title={config.description} arrow>
            <Box
              component="button"
              onClick={() => onStandardModeChange(mode)}
              aria-label={config.label}
              aria-pressed={isActive}
              sx={(theme) => ({
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: 'none',
                background: isActive
                  ? (theme.palette.mode === 'light'
                    ? theme.palette.action.hover
                    : 'rgba(255,255,255,.08)')
                  : 'transparent',
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                transition: 'background 0.15s, color 0.15s',
                '& svg': { fontSize: 13 },
                '&:hover': {
                  background: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
              })}
            >
              {React.cloneElement(config.icon, { fontSize: 'inherit' })}
              {showLabels && config.label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default ViewModeToggle;
